'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  ArrowRight, 
  UserPlus, 
  FileCheck, 
  Handshake, 
  ShieldAlert, 
  Zap, 
  Clock, 
  Ban, 
  TrendingDown, 
  CheckCircle2, 
  Globe, 
  Layers, 
  Activity, 
  Headset, 
  LayoutDashboard, 
  Search,
  UserMinus
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';

export default function AgencyRoadmapPage() {
  const { user, isLoggedIn } = useAuth();
  const { language } = useLanguage();
  const t = agencyTranslations.roadmapPage;
  
  const status = user?.partnershipStatus;
  const currentYear = new Date().getFullYear();
  const ctaLink = "/doi-tac/viec-lam";

  const renderHeroButtons = () => {
    if (status === 'APPROVED') {
      return (
        <>
          <Button asChild size="lg" className="bg-[#AFC536] hover:bg-[#AFC536]/90 text-white font-bold h-12 px-8 rounded-xl shadow-xl shadow-black/10 gap-2">
            <Link href="/doi-tac/viec-lam">
              <LayoutDashboard className="h-5 w-5" />
              {t.hero.dashboard[language]}
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-12 px-8 rounded-xl backdrop-blur-sm transition-all gap-2">
            <Link href="/doi-tac/ung-vien">
              <Search className="h-5 w-5" />
              {t.hero.manageCandidates[language]}
            </Link>
          </Button>
        </>
      );
    }

    if (status === 'PENDING') {
      return (
        <>
          <Button asChild size="lg" className="bg-[#F2B92A]/20 text-white border border-[#F2B92A]/50 hover:bg-[#F2B92A]/30 font-bold h-12 px-8 rounded-xl backdrop-blur-sm gap-2">
            <Link href="/doi-tac">
              <Clock className="h-5 w-5 animate-pulse" />
              {t.hero.pendingStatus[language]}
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-12 px-8 rounded-xl backdrop-blur-sm transition-all">
            <Link href="#pain-points">{t.hero.ctaProcess[language]}</Link>
          </Button>
        </>
      );
    }

    return (
      <>
        <Button asChild size="lg" className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-bold h-12 px-8 rounded-xl shadow-xl shadow-black/10">
          <Link href={ctaLink}>{t.hero.ctaStart[language]}</Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 h-12 px-8 rounded-xl backdrop-blur-sm transition-all">
          <Link href="#pain-points">{t.hero.ctaProcess[language]}</Link>
        </Button>
      </>
    );
  };

  const renderGlobalCTAButtons = () => {
    if (status === 'APPROVED') {
      return (
        <>
          <Button asChild size="lg" className="bg-[#0D8DC8] hover:bg-[#0D8DC8]/90 text-white font-bold h-16 px-12 rounded-2xl text-xl shadow-2xl transition-all hover:scale-105 gap-2">
            <Link href="/doi-tac/viec-lam">
              <LayoutDashboard className="h-6 w-6" />
              {t.hero.dashboard[language]}
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="bg-white border-white text-[#0D8DC8] hover:bg-slate-50 font-bold h-16 px-12 rounded-2xl text-xl shadow-xl gap-2">
            <Link href="/doi-tac/ung-vien">
              <Search className="h-6 w-6" />
              {t.hero.manageCandidates[language]}
            </Link>
          </Button>
        </>
      );
    }

    if (status === 'PENDING') {
      return (
        <Button asChild size="lg" className="bg-white text-[#F2B92A] hover:bg-white/90 font-bold h-12 px-8 rounded-xl text-lg shadow-2xl transition-all hover:scale-105 gap-2">
          <Link href="/doi-tac">
            <Clock className="h-6 w-6 animate-pulse" />
            {t.hero.pendingStatus[language]}
          </Link>
        </Button>
      );
    }

    return (
      <>
        <Button asChild size="lg" className="bg-[#0D8DC8] hover:bg-[#0D8DC8]/90 text-white font-bold h-12 px-10 rounded-xl text-lg shadow-2xl transition-all hover:scale-105">
           <Link href={ctaLink}>{t.hero.registerNowLong[language]}</Link>
        </Button>
        <Button asChild size="lg" variant="outline" className="bg-white border-white text-[#0D8DC8] hover:bg-slate-50 font-bold h-12 px-10 rounded-xl text-lg shadow-xl">
           <Link href="/doi-tac">{t.hero.backHome[language]}</Link>
        </Button>
      </>
    );
  };

  const painPoints = [
    {
      icon: <UserMinus className="h-10 w-10 text-[#F2B92A]" />,
      title: t.painPoints.item1.title[language],
      description: t.painPoints.item1.desc[language]
    },
    {
      icon: <Clock className="h-10 w-10 text-[#0D8DC8]" />,
      title: t.painPoints.item2.title[language],
      description: t.painPoints.item2.desc[language]
    },
    {
      icon: <Ban className="h-10 w-10 text-[#AFC536]" />,
      title: t.painPoints.item3.title[language],
      description: t.painPoints.item3.desc[language]
    },
    {
      icon: <TrendingDown className="h-10 w-10 text-[#19A6DF]" />,
      title: t.painPoints.item4.title[language],
      description: t.painPoints.item4.desc[language]
    }
  ];

  const solutions = [
    {
      icon: <Zap className="h-10 w-10 text-[#F2B92A]" />,
      title: t.solutions.item1.title[language],
      description: t.solutions.item1.desc[language]
    },
    {
      icon: <Layers className="h-10 w-10 text-[#F2B92A]" />,
      title: t.solutions.item2.title[language],
      description: t.solutions.item2.desc[language]
    },
    {
      icon: <Activity className="h-10 w-10 text-[#F2B92A]" />,
      title: t.solutions.item3.title[language],
      description: t.solutions.item3.desc[language]
    },
    {
      icon: <Headset className="h-10 w-10 text-[#F2B92A]" />,
      title: t.solutions.item4.title[language],
      description: t.solutions.item4.desc[language]
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative w-full py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-[#19A6DF] to-[#0D8DC8] text-white">
        <div className="absolute inset-0 z-0 opacity-10">
          <Image 
            src="/img/BANGLADESH/TRANGVIETNAM/doi-tac-quy-trinh1.jpg" 
            alt="Bangladesh Hero" 
            fill 
            className="object-cover grayscale"
          />
        </div>
        
        <div className="container relative z-10 mx-auto px-4 md:px-6">
          <div className="max-w-3xl space-y-6 animate-in fade-in slide-in-from-left-8 duration-700">
            <div className="inline-block rounded-full bg-white/20 px-4 py-1.5 text-sm font-bold text-white border border-white/30 backdrop-blur-sm">
              {t.hero.badge[language].replace('{year}', currentYear.toString())}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold font-headline tracking-tight leading-tight">
              {t.hero.title[language]} <br />
              <span className="text-[#F2B92A]">{t.hero.highlight[language]}</span>{t.hero.titleSuffix[language]}
            </h1>
            <p className="text-base md:text-lg text-white/90 max-w-2xl leading-relaxed">
              {t.hero.subtitle[language]}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4 w-full sm:w-auto">
              {renderHeroButtons()}
            </div>
          </div>
        </div>
        
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/4 w-64 h-64 bg-[#AFC536]/20 rounded-full blur-3xl" />
      </section>

      {/* Pain Points Section */}
      <section id="pain-points" className="w-full py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-[#0D8DC8] px-4 md:px-0 leading-tight">{t.painPoints.title[language]}</h2>
            <div className="h-1.5 w-24 bg-[#F2B92A] mx-auto rounded-full" />
            <p className="text-[#0D8DC8]/70 text-base md:text-lg font-medium">{t.painPoints.subtitle[language]}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {painPoints.map((item, index) => (
              <Card key={index} className="border-none shadow-md hover:shadow-xl transition-all duration-500 hover:-translate-y-2 rounded-3xl overflow-hidden bg-white group animate-in fade-in slide-in-from-bottom-10 fill-mode-both" style={{ animationDelay: `${index * 150}ms` }}>
                <CardContent className="p-8 space-y-4">
                  <div className="mb-6 bg-slate-50 p-4 rounded-2xl w-fit group-hover:scale-110 transition-all duration-500">
                    <div className="group-even:text-[#AFC536] group-odd:text-[#0D8DC8]">{item.icon}</div>
                  </div>
                  <h3 className="text-2xl font-bold font-headline text-slate-800">{item.title}</h3>
                  <p className="text-slate-500 text-base leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="w-full py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 relative">
               <div className="absolute -top-12 -left-12 w-64 h-64 bg-[#19A6DF]/10 rounded-full blur-3xl animate-pulse" />
               <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-slate-50">
                  <Image 
                    src="/img/BANGLADESH/TRANGVIETNAM/doi-tac-chibi.jpg"
                    alt={agencyTranslations.alt.solution[language]}
                    width={600}
                    height={450}
                    className="object-cover"
                  />
               </div>
            </div>
            
            <div className="lg:w-1/2 space-y-10">
              <div className="space-y-4">
                <h2 className="text-3xl md:text-4xl font-headline font-bold text-[#0D8DC8] tracking-tight leading-tight">{t.solutions.title[language]}</h2>
                <p className="text-[#0D8DC8]/70 text-xl">{t.solutions.subtitle[language]}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {solutions.map((item, index) => (
                  <div key={index} className="space-y-3 group animate-in fade-in slide-in-from-right-10 fill-mode-both" style={{ animationDelay: `${(index + 2) * 150}ms` }}>
                    <div className="p-3 bg-[#19A6DF]/10 rounded-2xl w-fit group-hover:bg-[#19A6DF]/20 transition-colors">
                      <div className="text-[#19A6DF]">{item.icon}</div>
                    </div>
                    <h3 className="font-bold text-lg text-slate-800">{item.title}</h3>
                    <p className="text-slate-500 text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
              
              <Button asChild size="lg" className="bg-[#0D8DC8] hover:bg-[#19A6DF] text-white font-bold h-12 px-10 rounded-xl shadow-xl shadow-[#0D8DC8]/20 transition-all">
                <Link href={ctaLink}>{t.solutions.cta[language]}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed 3 Steps Section */}
      <section className="w-full py-24 bg-slate-50 relative">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-24 space-y-4">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-[#0D8DC8] leading-tight px-4">{t.steps.title[language]}</h2>
            <div className="h-1.5 w-32 bg-[#F2B92A] mx-auto rounded-full" />
            <p className="text-[#0D8DC8]/70 text-xl">{t.steps.subtitle[language]}</p>
          </div>
          
          <div className="space-y-24">
             {/* Step 1 - Theme Orange */}
             <div className="flex flex-col md:flex-row items-center gap-12 group p-8 lg:p-12 rounded-[3rem] bg-white shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 animate-in fade-in slide-in-from-left-10 fill-mode-both">
                <div className="md:w-1/3 flex justify-center">
                   <div className="relative">
                      <div className="w-40 h-40 rounded-full bg-[#F2B92A] flex items-center justify-center text-white text-6xl font-bold shadow-2xl shadow-[#F2B92A]/30 group-hover:scale-105 transition-transform">1</div>
                      <div className="absolute inset-0 rounded-full border-4 border-[#F2B92A] animate-ping opacity-10" />
                   </div>
                </div>
                <div className="md:w-2/3 space-y-6">
                   <h3 className="text-2xl md:text-3xl font-bold font-headline flex items-center gap-4 text-slate-900">
                      {t.steps.step1.title[language]}
                      <UserPlus className="h-8 w-8 text-[#F2B92A]" />
                   </h3>
                   <div className="space-y-4 text-slate-600 text-xl leading-relaxed">
                      <p>{t.steps.step1.desc[language]}</p>
                      <ul className="space-y-3 text-lg">
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step1.bullet1[language]}</li>
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step1.bullet2[language]}</li>
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step1.bullet3[language]}</li>
                      </ul>
                   </div>
                   <Button asChild size="lg" className="bg-[#F2B92A] hover:bg-[#F2B92A]/90 text-white font-bold rounded-xl h-12 shadow-lg shadow-[#F2B92A]/20 w-full sm:w-auto px-8 transition-all">
                      <Link href={ctaLink}>{t.steps.step1.cta[language]} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                   </Button>
                </div>
             </div>

             {/* Step 2 - Theme Blue */}
             <div className="flex flex-col md:flex-row-reverse items-center gap-12 group p-8 lg:p-12 rounded-[3rem] bg-white shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 animate-in fade-in slide-in-from-right-10 fill-mode-both">
                <div className="md:w-1/3 flex justify-center">
                   <div className="relative">
                      <div className="w-40 h-40 rounded-full bg-[#0D8DC8] flex items-center justify-center text-white text-6xl font-bold shadow-2xl shadow-[#0D8DC8]/30 group-hover:scale-105 transition-transform">2</div>
                      <div className="absolute inset-0 rounded-full border-4 border-[#0D8DC8] animate-ping opacity-10" />
                   </div>
                </div>
                <div className="md:w-2/3 space-y-6 text-right">
                   <h3 className="text-2xl md:text-3xl font-bold font-headline flex items-center justify-end gap-4 text-slate-900">
                      <FileCheck className="h-8 w-8 text-[#0D8DC8]" />
                      {t.steps.step2.title[language]}
                   </h3>
                   <div className="space-y-4 text-slate-600 text-xl leading-relaxed">
                      <p>{t.steps.step2.desc[language]}</p>
                      <ul className="space-y-3 flex flex-col items-end text-lg">
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step2.bullet1[language]}</li>
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step2.bullet2[language]}</li>
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step2.bullet3[language]}</li>
                      </ul>
                   </div>
                   <Button asChild size="lg" variant="outline" className="text-[#0D8DC8] border-[#0D8DC8] hover:bg-[#0D8DC8] hover:text-white font-bold rounded-xl h-12 px-8 transition-colors w-full sm:w-auto">
                      <Link href={ctaLink}>{t.steps.step2.cta[language]} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                   </Button>
                </div>
             </div>

             {/* Step 3 - Theme Green */}
             <div className="flex flex-col md:flex-row items-center gap-12 group p-8 lg:p-12 rounded-[3rem] bg-white shadow-sm border border-slate-100 hover:shadow-xl transition-all duration-500 animate-in fade-in slide-in-from-left-10 fill-mode-both">
                <div className="md:w-1/3 flex justify-center">
                   <div className="relative">
                      <div className="w-40 h-40 rounded-full bg-[#AFC536] flex items-center justify-center text-white text-6xl font-bold shadow-2xl shadow-[#AFC536]/30 group-hover:scale-105 transition-transform">3</div>
                      <div className="absolute inset-0 rounded-full border-4 border-[#AFC536] animate-ping opacity-10" />
                   </div>
                </div>
                <div className="md:w-2/3 space-y-6">
                   <h3 className="text-2xl md:text-3xl font-bold font-headline flex items-center gap-4 text-slate-900">
                      {t.steps.step3.title[language]}
                      <Handshake className="h-8 w-8 text-[#AFC536]" />
                   </h3>
                   <div className="space-y-4 text-slate-600 text-xl leading-relaxed">
                      <p>{t.steps.step3.desc[language]}</p>
                      <ul className="space-y-3 text-lg">
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step3.bullet1[language]}</li>
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step3.bullet2[language]}</li>
                         <li className="flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-[#AFC536]" /> {t.steps.step3.bullet3[language]}</li>
                      </ul>
                   </div>
                   <Button asChild size="lg" className="bg-[#AFC536] hover:bg-[#AFC536]/90 text-white font-bold rounded-xl h-12 shadow-lg shadow-[#AFC536]/30 w-full sm:w-auto px-8 transition-all">
                      <Link href={ctaLink}>{t.steps.step3.cta[language]} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                   </Button>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Global CTA Section - Theme Orange Contrast */}
      <section className="w-full py-24 bg-[#F2B92A] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="flex flex-col items-center text-center space-y-8">
            <h2 className="text-3xl md:text-4xl font-bold font-headline text-white leading-tight px-4">
              {t.finalCta.titlePrefix[language]} <br /> {t.finalCta.titleSuffix[language]}
            </h2>
            <p className="text-lg md:text-xl text-white/90 font-medium max-w-2xl">{t.finalCta.desc[language]}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-6 pt-4 w-full px-4 sm:px-0">
               {renderGlobalCTAButtons()}
            </div>
            <div className="flex items-center gap-3 text-white font-bold mt-8 bg-white/20 px-6 py-2 rounded-full backdrop-blur-sm">
               <Globe className="h-5 w-5" />
               {t.finalCta.footer[language]}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
